import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { devLog } from "@/lib/log";
import {
  isPushSupported,
  getPushStatus,
  subscribePush,
  unsubscribePush,
  sendTestPush,
  sweepPush,
  ensureServiceWorker,
} from "@/lib/push";
import { Bell, BellOff, Send, ShieldAlert, Loader2, Wind, Smartphone } from "lucide-react";

export default function PushToggle() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [lastSweep, setLastSweep] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await getPushStatus();
      setStatus(s);
    } catch (err) {
      devLog.warn("push status failed", err);
      setStatus({ supported: false, permission: "denied", subscribed: false });
    }
  }, []);

  useEffect(() => {
    // Register the SW eagerly so subscribing later is a one-tap flow.
    if (isPushSupported()) ensureServiceWorker().catch(() => {});
    load();
  }, [load]);

  if (!status) {
    return (
      <div className="bi-surface p-4 space-y-2" data-testid="push-toggle">
        <div className="text-white/50 text-sm">Checking push support…</div>
      </div>
    );
  }

  if (!status.supported) {
    return (
      <div className="bi-surface p-4 space-y-2" data-testid="push-toggle">
        <div className="flex items-center gap-2 text-[#FBBF24]">
          <ShieldAlert className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-heading">
            Push not supported
          </span>
        </div>
        <div className="text-sm text-white/70 leading-snug">
          This browser doesn&apos;t support Web Push. Install BuildIntel to your home screen or open
          it in Chrome/Edge/Firefox to get notifications.
        </div>
      </div>
    );
  }

  const blocked = status.permission === "denied";

  const toggle = async () => {
    setBusy(true);
    try {
      if (status.subscribed) {
        await unsubscribePush();
        toast("Push notifications turned off");
      } else {
        await subscribePush();
        toast.success("Push notifications on — we'll ping you for job health & tax alerts");
      }
      await load();
    } catch (err) {
      devLog.warn("toggle push failed", err);
      toast.error(err.message || "Push toggle failed");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTestBusy(true);
    try {
      await sendTestPush();
      toast.success("Test push sent — check your notification tray");
    } catch (err) {
      devLog.warn("test push failed", err);
      toast.error(err.response?.data?.detail || err.message || "Test push failed");
    } finally {
      setTestBusy(false);
    }
  };

  const sweep = async () => {
    setSweepBusy(true);
    try {
      const r = await sweepPush();
      setLastSweep(r);
      const removed = (r?.expired_removed || 0) + (r?.failed_removed || 0);
      if (removed > 0) {
        toast.success(
          `Cleaned up ${removed} stale device${removed === 1 ? "" : "s"} · ${r.remaining} still active`
        );
      } else {
        toast(`All ${r?.remaining || 0} device${r?.remaining === 1 ? "" : "s"} healthy · nothing to sweep`);
      }
      await load();
    } catch (err) {
      devLog.warn("sweep push failed", err);
      toast.error(err.response?.data?.detail || err.message || "Sweep failed");
    } finally {
      setSweepBusy(false);
    }
  };

  return (
    <section className="bi-surface p-4 space-y-3" data-testid="push-toggle">
      <div className="flex items-center gap-2 text-[#FF9500]">
        <Bell className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-heading">
          Push notifications
        </span>
      </div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-base">
            {status.subscribed ? "You'll be pinged" : "Get pinged when it matters"}
          </div>
          <div className="text-xs text-white/60 leading-snug mt-0.5">
            Real browser notifications for job health warnings and new ATO rulings. Works even when
            the app is closed.
          </div>
          {blocked && (
            <div className="mt-2 text-[11px] text-[#F87171] inline-flex items-start gap-1.5">
              <ShieldAlert className="w-3 h-3 mt-0.5" />
              Notifications are blocked in your browser — enable them in site settings first.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy || blocked}
          data-testid="push-toggle-btn"
          className={`h-12 w-16 rounded-xl flex items-center justify-center shrink-0 transition disabled:opacity-50 ${
            status.subscribed ? "bg-[#34D399] text-[#111827]" : "bi-ghost-btn"
          }`}
          title={status.subscribed ? "Turn off push" : "Turn on push"}
        >
          {busy ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : status.subscribed ? (
            <Bell className="w-5 h-5" />
          ) : (
            <BellOff className="w-5 h-5" />
          )}
        </button>
      </div>

      {status.subscribed && (
        <button
          type="button"
          onClick={test}
          disabled={testBusy}
          data-testid="push-test-btn"
          className="w-full h-10 rounded-lg bi-ghost-btn text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {testBusy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
          Send test push
        </button>
      )}

      {status.server_count > 0 && (
        <div
          data-testid="push-sweep-tile"
          className="mt-1 rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-[#FF9500]/12 text-[#FF9500] flex items-center justify-center shrink-0">
            <Smartphone className="w-4.5 h-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div
              data-testid="push-sweep-count"
              className="text-sm font-heading font-extrabold text-white leading-tight"
            >
              {status.server_count} device{status.server_count === 1 ? "" : "s"} registered
            </div>
            <div className="text-[11px] text-white/50 leading-snug mt-0.5">
              {lastSweep
                ? lastSweep.expired_removed + lastSweep.failed_removed > 0
                  ? `Last sweep · cleared ${lastSweep.expired_removed + lastSweep.failed_removed}`
                  : "Last sweep · all healthy"
                : "Removes expired subs so pushes never bounce silently."}
            </div>
          </div>
          <button
            type="button"
            onClick={sweep}
            disabled={sweepBusy}
            data-testid="push-sweep-btn"
            className="h-10 px-3 rounded-lg bi-ghost-btn text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50 shrink-0"
            title="Sweep stale push subscriptions"
          >
            {sweepBusy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Wind className="w-3.5 h-3.5" />
            )}
            Sweep
          </button>
        </div>
      )}
    </section>
  );
}
